import { TIERS } from '../content'
import type { GroupId } from './systemConfig'

/**
 * Application-wide constants.
 * Centralized location for magic numbers and configuration values.
 */

// Storage
export const STORAGE_KEY = 'meester_djay_v3'

/** Application display name — single source of truth */
export const APP_NAME = 'Meester djay'

// ── Account & Auth Constants ─────────────────────────────────────────

/** HC-5: Minimum password lengths per role */
export const PASSWORD_MIN_LENGTH = {
  learner: 4,
  default: 6,
} as const

/** HC-6: Minimum display name length */
export const DISPLAY_NAME_MIN_LENGTH = 2

/** HC-4: Maximum display name length */
export const DISPLAY_NAME_MAX_LENGTH = 50

/** HC-3: Maximum retries when generating a unique link code */
export const LINK_CODE_MAX_RETRIES = 10

/** HC-2: Session timeout durations per role (milliseconds) */
export const SESSION_TIMEOUT_MS = {
  learner: 30 * 60 * 1000, // 30 minutes
  default: 60 * 60 * 1000, // 60 minutes
} as const

/** HC-1: Interval for checking session timeout (milliseconds) */
export const SESSION_CHECK_INTERVAL_MS = 60_000 // 1 minute

/** HC-12: Maximum age for a recoverable assessment session (milliseconds) */
export const ASSESSMENT_SESSION_MAX_AGE_MS = 2 * 60 * 60 * 1000 // 2 hours

// ── UI / Portal Constants ────────────────────────────────────────────

/** HC-9: Roles that have their own portal view */
export const PORTAL_ROLES = ['teacher', 'parent', 'admin'] as const

/** HC-10: Human-readable portal labels per role */
export const PORTAL_LABELS: Record<string, string> = {
  learner: 'Leerling',
  parent: 'Ouderportaal',
  teacher: 'Docentportaal',
  admin: 'Beheer',
}

/** HC-7: Default group for new sessions */
export const DEFAULT_GROUP: GroupId = 6

/** HC-8: Default learner name for initial state */
export const DEFAULT_LEARNER_NAME = 'Djayden'

// ── XP & Leveling ────────────────────────────────────────────────────

// V2 Progressive Level System (15 levels)
export const LEVEL_THRESHOLDS = [
  0, // Level 1
  150, // Level 2
  400, // Level 3
  800, // Level 4
  1400, // Level 5
  2200, // Level 6
  3200, // Level 7
  4500, // Level 8
  6000, // Level 9
  8000, // Level 10
  11000, // Level 11
  15000, // Level 12
  20000, // Level 13
  30000, // Level 14
  45000, // Level 15
] as const

export const MAX_LEVEL = 15

export function getLevelForXP(xp: number): number {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) return i + 1
  }
  return 1
}

export function getProgressToNextLevel(xp: number): {
  current: number
  needed: number
  percent: number
} {
  const level = getLevelForXP(xp)
  if (level >= MAX_LEVEL) return { current: 0, needed: 0, percent: 100 }

  const currentThreshold = LEVEL_THRESHOLDS[level - 1]
  const nextThreshold = LEVEL_THRESHOLDS[level]
  const current = xp - currentThreshold
  const needed = nextThreshold - currentThreshold

  return { current, needed, percent: (current / needed) * 100 }
}

// XP Rewards
export const XP_REWARDS = {
  CORRECT_HARD: 40, // Tier 13-16
  CORRECT_MEDIUM: 25, // Tier 7-12
  CORRECT_EASY: 15, // Tier 1-6
  EFFORT: 5, // Wrong answer (participation)
  // Bonuses
  DAILY_GOAL: 50,
  PERFECT_SESSION: 30,
  STREAK_5: 10,
  STREAK_10: 25,
  WEEK_3_DAYS: 25,
  WEEK_5_DAYS: 75,
} as const

/**
 * Calculate XP earned for a single answer.
 * Canonical implementation — use this instead of inline tier logic.
 * Consolidated from PracticeSession.tsx and learnerActions.ts (D-SCAN D-4).
 */
export function calculateXP(isCorrect: boolean, tier?: number): number {
  if (!isCorrect) return XP_REWARDS.EFFORT
  if (!tier) return XP_REWARDS.CORRECT_EASY
  if (tier >= 13) return XP_REWARDS.CORRECT_HARD
  if (tier >= 7) return XP_REWARDS.CORRECT_MEDIUM
  return XP_REWARDS.CORRECT_EASY
}

/**
 * Mastery level display thresholds (D-SCAN D-5).
 * Single source of truth for the 40/70/85 boundaries used in UI display.
 * NOT the same as MASTERY_THRESHOLDS (which tracks module-level completion).
 *
 * NOTE: V11_CONFIG in mastery/selection/selectionConfig.ts defines the same
 * boundaries (STRUGGLING: 40, LEARNING: 70, MASTERED: 85) for the adaptive
 * selection engine. These are intentionally separate: UI display vs engine logic.
 * If pedagogical boundaries change, update BOTH locations. (D-SCAN-2 D2-5)
 */
export const MASTERY_LEVEL_THRESHOLDS = {
  BEGINNER: 40, // < 40 = beginner
  OEFENAAR: 70, // 40–69 = oefenaar
  GEVORDERD: 85, // 70–84 = gevorderd
  MEESTER: 85, // >= 85 = meester
} as const

// Session Configuration
export const SESSION_CONFIG = {
  QUESTIONS_PER_SESSION: 10,
  RECENT_QUESTIONS_LIMIT: 25,
  // Increased from 15 to prevent loops in small pools
  CITO_QUESTION_COUNT: 30,
  CITO_TIME_LIMIT_SECONDS: 45 * 60, // 45 minutes
  DAILY_GOAL: 10, // Questions per day target for streak/progress
} as const

// Assessment Cooldown (per-group)
export const ASSESSMENT_COOLDOWN = {
  /** Minimum time between assessments for the same group (in milliseconds) */
  MIN_INTERVAL_MS: 60 * 60 * 1000, // 1 hour
  /** Human-readable label for the cooldown duration */
  LABEL: '1 uur',
} as const

// Mastery Thresholds (aligned with progressive mastery model)
export const MASTERY_THRESHOLDS = {
  MASTERED: 95,
  // Near-complete mastery of group content
  PRACTICED: 60,
  // Showing consistency
  LEARNING: 0, // Has started but not consistent
} as const

// Difficulty Labels (for display)
// Mapped from the centralized configuration
export const DIFFICULTY_LABELS: Record<number, string> = TIERS.reduce(
  (acc, tier) => {
    acc[tier.tier] = tier.levelCode
    return acc
  },
  {} as Record<number, string>,
)
